import React, { useMemo } from "react";
import {
  Card,
  Typography,
  Table,
  Tag,
  Space,
  Divider,
  Alert,
  Button,
  message,
} from "antd";
import { BookOutlined, CopyOutlined } from "@ant-design/icons";
import {
  GLOSSARY_SECTIONS,
  buildGlossaryMarkdown,
  type GlossaryRow,
} from "./partnerApiGlossaryData";

const { Title, Text, Paragraph } = Typography;

function GlossaryTable({
  title,
  rows,
  intro,
}: {
  title: string;
  rows: GlossaryRow[];
  intro?: string;
}) {
  return (
    <Card size="small" style={{ marginBottom: 16 }} title={title}>
      {intro && (
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {intro}
        </Paragraph>
      )}
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={rows}
        columns={[
          {
            title: "Field",
            dataIndex: "field",
            width: "28%",
            render: (v: string) => <Text code>{v}</Text>,
          },
          {
            title: "Meaning",
            dataIndex: "meaning",
          },
        ]}
      />
    </Card>
  );
}

function buildPlainText(): string {
  const md = buildGlossaryMarkdown();
  return (
    md
      .replace(/^#+\s*/gm, "")
      .replace(/^\|\s*/gm, "")
      .replace(/\s*\|$/gm, "")
      .replace(/\| --- \| --- \|/g, "")
      .replace(/\|/g, " — ")
      .replace(/`/g, "")
      .replace(/^>\s*/gm, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim() + "\n"
  );
}

/** Field glossary for partner API request/response shapes. */
export const PartnerApiGlossary: React.FC<{ publicPage?: boolean }> = ({
  publicPage = false,
}) => {
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      Array<(typeof GLOSSARY_SECTIONS)[number]>
    >();
    for (const section of GLOSSARY_SECTIONS) {
      const list = map.get(section.heading) ?? [];
      list.push(section);
      map.set(section.heading, list);
    }
    return [...map.entries()];
  }, []);

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(buildGlossaryMarkdown());
      message.success("Glossary copied as Markdown");
    } catch {
      message.error("Could not copy");
    }
  };

  const copyPlain = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      message.success("Glossary copied as plain text");
    } catch {
      message.error("Could not copy");
    }
  };

  return (
    <div
      style={{
        maxWidth: publicPage ? 920 : undefined,
        margin: publicPage ? "0 auto" : undefined,
        width: "100%",
      }}
    >
      <Card
        title={
          <Space>
            <BookOutlined />
            <span>Glossary</span>
            <Tag>Partner API</Tag>
          </Space>
        }
        extra={
          <Space wrap>
            <Button type="primary" icon={<CopyOutlined />} onClick={copyMarkdown}>
              Copy Markdown
            </Button>
            <Button onClick={copyPlain}>Copy plain text</Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Paragraph style={{ marginBottom: 8 }}>
          Plain-language meanings for fields that often confuse integrators —
          especially the full booking JSON from{" "}
          <Text code>bookings.get</Text> / <Text code>bookings.lookup</Text>.
          Use the copy buttons to paste into Slack, Notion, or an email.
        </Paragraph>
        <Alert
          type="info"
          showIcon
          message="Dates vs reality"
          description="ETD/ETA values are estimates. Compare them to “today” (Europe/Amsterdam) before saying something already happened. Prefer customer_eta and effective_etd when answering end users."
        />
      </Card>

      {grouped.map(([heading, sections], index) => (
        <React.Fragment key={heading}>
          {index > 0 && heading === "Full booking JSON" && <Divider />}
          <Title level={5}>{heading}</Title>
          {heading === "Full booking JSON" && (
            <Paragraph type="secondary">
              Returned by <Text code>bookings.get</Text> and{" "}
              <Text code>bookings.lookup</Text> (enriched). Nested structure
              mirrors the customer booking view in the dashboard.
            </Paragraph>
          )}
          {sections.map((section) => (
            <GlossaryTable
              key={`${section.heading}-${section.title}`}
              title={section.title}
              intro={
                // Avoid duplicating the full-booking intro under Identity
                section.heading === "Full booking JSON" &&
                section.title === "Identity & status"
                  ? undefined
                  : section.intro
              }
              rows={section.rows}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};
